import React, { useEffect, useState } from "react";
import {
  profile,
  about,
  experience,
  projects,
  skills,
  education,
  certifications,
  links,
} from "../content_option";
import "./App.css";

// Old multi-page URLs still resolve (404.html serves this app); send them to the matching section.
const legacyPaths = { "/about": "about", "/portfolio": "projects" };

function readTheme() {
  try {
    const saved = localStorage.getItem("theme");
    if (saved === "light" || saved === "dark") return saved;
  } catch (e) {}
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

export default function App() {
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem("theme", theme);
    } catch (e) {}
  }, [theme]);

  useEffect(() => {
    const path = window.location.pathname.replace(process.env.PUBLIC_URL || "", "").replace(/\/$/, "");
    const target = legacyPaths[path];
    if (target) {
      window.history.replaceState(null, "", `${process.env.PUBLIC_URL || ""}/#${target}`);
      document.getElementById(target)?.scrollIntoView();
    }
  }, []);

  const year = new Date().getFullYear();

  return (
    <>
      <header className="nav">
        <div className="wrap nav__inner">
          <a href="#top" className="nav__brand">{profile.name}</a>
          <nav className="nav__links">
            <a href="#experience">Experience</a>
            <a href="#projects">Projects</a>
            <a href="#education">Education</a>
            <button
              className="theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
              {theme === "dark" ? "☀" : "☾"}
            </button>
          </nav>
        </div>
      </header>

      <main id="top" className="wrap">
        <section className="hero">
          <img src={profile.photo} alt={profile.name} className="hero__photo" />
          <div>
            <h1>{profile.name}</h1>
            <p className="hero__role">{profile.role} · {profile.location}</p>
            <p className="hero__intro">{profile.intro}</p>
            <ul className="link-row">
              {links.map((l) => (
                <li key={l.label}>
                  <a href={l.url} target="_blank" rel="noopener noreferrer">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <Section id="about" title="About">
          <p>{about}</p>
          <ul className="tags" aria-label="Skills">
            {skills.map((s) => <li key={s}>{s}</li>)}
          </ul>
        </Section>

        <Section id="experience" title="Experience">
          <ul className="rows">
            {experience.map((job) => (
              <li key={job.company + job.date} className="row">
                <div>
                  <strong>{job.role}</strong>
                  <span className="muted"> · {job.company}</span>
                </div>
                <span className="muted date">{job.date}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section id="projects" title="Projects">
          <ul className="cards">
            {projects.map((p) => (
              <li key={p.name}>
                <a href={p.link} target="_blank" rel="noopener noreferrer" className="card">
                  <strong>{p.name} <span aria-hidden="true">↗</span></strong>
                  {p.meta && <span className="card-meta muted">{p.meta}</span>}
                  <span className="muted">{p.description}</span>
                </a>
              </li>
            ))}
          </ul>
        </Section>

        <Section id="education" title="Education">
          <ul className="rows">
            {education.map((e) => (
              <li key={e.title} className="row">
                <strong>{e.title}</strong>
                <span className="muted">{e.school}</span>
              </li>
            ))}
          </ul>
          <h3>Certifications</h3>
          <ul className="rows">
            {certifications.map((c) => (
              <li key={c.title} className="row">
                <strong>{c.title}</strong>
                <span className="muted">{c.issuer}</span>
              </li>
            ))}
          </ul>
        </Section>
      </main>

      <footer className="wrap footer">
        <span>
          © {year} {profile.name} · Powered by{" "}
          <a href="https://quabcore.com" target="_blank" rel="noopener noreferrer">Quabcore Technologies</a>
        </span>
        <ul className="link-row">
          {links.map((l) => (
            <li key={l.label}>
              <a href={l.url} target="_blank" rel="noopener noreferrer">{l.label}</a>
            </li>
          ))}
        </ul>
      </footer>
    </>
  );
}
